import {
  ApiError,
  type ErrorKind,
  type ErrorPresentation,
  classifyHttpStatus,
  isRetryable,
} from '../../../shared/errors/index';

export type ApiServiceErrorKind = 'circuit_breaker' | 'http' | 'network' | 'unknown';

export interface ApiRequestMeta {
  method?: string;
  url?: string;
  baseURL?: string;
}

/**
 * Typed error thrown by the frontend API service layer.
 * Extends the shared `ApiError` base class so that `instanceof ApiError` works
 * as a universal API error guard across frontend, mobile, and shared utilities.
 */
export class ApiServiceError extends ApiError {
  /** Service-layer kind (superset of ErrorKind — adds 'http' for unclassified HTTP errors) */
  serviceKind: ApiServiceErrorKind;
  request?: ApiRequestMeta;
  originalError?: unknown;

  constructor(
    message: string,
    opts: {
      kind: ApiServiceErrorKind;
      status?: number;
      code?: string;
      request?: ApiRequestMeta;
      responseData?: unknown;
      originalError?: unknown;
    }
  ) {
    // Map ApiServiceErrorKind → shared ErrorKind for the ApiError base
    const mappedKind: ErrorKind =
      opts.kind === 'circuit_breaker'
        ? 'circuit_breaker'
        : opts.kind === 'network'
          ? 'network'
          : opts.kind === 'http' && typeof opts.status === 'number'
            ? classifyHttpStatus(opts.status)
            : 'unknown';

    const presentation: ErrorPresentation = {
      kind: mappedKind,
      friendlyMessage: message,
      status: opts.status,
      code: opts.code,
      retryable: isRetryable(mappedKind),
    };

    super(presentation, opts.responseData);
    this.name = 'ApiServiceError';
    this.serviceKind = opts.kind;
    this.request = opts.request;
    this.originalError = opts.originalError;

    Object.setPrototypeOf(this, new.target.prototype);
  }
}

export function createCircuitBreakerError(args: {
  friendlyMessage: string;
  status: number;
  request?: ApiRequestMeta;
  responseData?: unknown;
  originalError?: unknown;
}): ApiServiceError {
  return new ApiServiceError(args.friendlyMessage, {
    kind: 'circuit_breaker',
    status: args.status,
    code: 'CIRCUIT_BREAKER',
    request: args.request,
    responseData: args.responseData,
    originalError: args.originalError,
  });
}
